import type { IconType } from 'react-icons'
import {
  TbWorld,
  TbChartBar,
  TbDeviceAnalytics,
  TbSparkles,
  TbEdit,
  TbHeartHandshake,
  TbMathFunction,
  TbSettings,
  TbStack2,
  TbUpload,
  TbBrowser,
  TbList,
  TbArrowsLeftRight,
  TbShield,
  TbBook2,
  TbUsersGroup,
  TbFileDescription,
  TbMap,
  TbBriefcase,
} from 'react-icons/tb'

import { CommunityIcon } from '../components-v2/icons/community'
import { OmniBadge } from '../components-v2/icons/omni-badge'
import { Snowflake } from '../components-v2/icons/snowflake'
import { Databricks } from '../components-v2/icons/databricks'
import { Integrations } from '../components-v2/icons/integrations'

export type NavItem = {
  label?: string
  to?: string // now optional
  icon?: IconType
  subTitle?: string
  children?: NavItem[]
  externalLink?: boolean
}

type Column = NavItem[]

type MenuItem = {
  title: string
  to?: string
  columns?: Column[]
  icon?: IconType
  subTitle?: string
  megaMenu?: boolean
  labelOnly?: boolean
  externalLink?: boolean
}

export const menuItems: MenuItem[] = [
  {
    title: 'Product',
    icon: TbWorld,
    megaMenu: true,
    columns: [
      [
        {
          label: 'Overview',
          to: '/platform',
          icon: TbWorld,
          subTitle: 'Data everyone can use',
        },
        {
          label: 'Business Intelligence',
          to: '/business-intelligence',
          icon: TbDeviceAnalytics,
          subTitle: 'Insights your team trusts',
        },
        {
          label: 'Embedded Analytics',
          to: '/embedded-analytics',
          icon: TbChartBar,
          subTitle: 'Customized data & AI in your product',
        },
        {
          label: 'AI',
          to: '/ai',
          icon: TbSparkles,
          subTitle: 'Ask questions, get answers you trust',
        },
      ],
      [
        {
          label: 'Integrations',
          to: '/integrations',
          icon: Integrations,
          subTitle: 'Connect to data sources',
        },
        {
          label: 'Capabilities',
          icon: TbSettings,
          children: [
            {
              label: 'Calculations',
              to: '/calculations',
              icon: TbMathFunction,
              subTitle: 'Excel formulas on live data',
            },
            {
              label: 'Data Modeling',
              to: '/data-modeling',
              icon: TbStack2,
              subTitle: 'Model data without slowing analysis',
            },
            {
              label: 'Data Input',
              to: '/data-input',
              icon: TbUpload,
              subTitle: 'Add external data, join, & analyze',
            },
            {
              label: 'See more in docs',
              to: 'https://docs.omni.co/docs',
              icon: TbFileDescription,
              externalLink: true,
            },
          ],
        },
      ],
    ],
  },
  {
    title: 'Blog',
    to: '/blog',
  },
  {
    title: 'Customers',
    icon: TbHeartHandshake,
    columns: [
      [
        {
          label: 'Case Studies',
          to: '/customer-case-studies',
          icon: TbEdit,
          subTitle: 'How customers use Omni',
        },
        {
          label: 'Customer Support',
          to: '/customer-support',
          icon: TbHeartHandshake,
          subTitle: 'White-glove help for every customer',
        },
      ],
    ],
  },
  {
    title: 'Resources',
    icon: TbMathFunction,
    megaMenu: true,
    columns: [
      [
        {
          label: 'Demos',
          to: '/demos',
          icon: TbBrowser,
          subTitle: 'Weekly product demo videos',
        },
        {
          label: 'Changelog',
          to: '/changelog',
          icon: TbList,
          subTitle: 'Latest features and updates',
        },
        {
          label: 'Compare',
          to: '/compare',
          icon: TbArrowsLeftRight,
          subTitle: 'Omni vs. other platforms',
        },
        {
          label: 'Security',
          to: '/security',
          icon: TbShield,
          subTitle: 'SOC2, HIPAA, & more',
        },
      ],
      [
        {
          label: 'Learn',
          icon: TbBook2,
          children: [
            {
              label: 'Documentation',
              subTitle: 'Setup and best practices',
              to: 'https://docs.omni.co/docs',
              icon: TbFileDescription,
              externalLink: true,
            },
            {
              label: 'User Guides',
              subTitle: 'Step-by-step tutorials',
              to: 'https://docs.omni.co/guides',
              icon: TbMap,
              externalLink: true,
            },
            {
              label: 'Community',
              subTitle: 'Share, learn, get help',
              to: 'https://community.omni.co/',
              icon: CommunityIcon,
              externalLink: true,
            },
          ],
        },
        {
          label: 'Partners',
          icon: TbUsersGroup,
          children: [
            {
              label: 'Snowflake',
              to: '/snowflake',
              icon: Snowflake,
            },
            {
              label: 'Databricks',
              to: '/databricks',
              icon: Databricks,
            },
          ],
        },
      ],
    ],
  },
  {
    title: 'Company',
    icon: TbEdit,
    columns: [
      [
        {
          label: 'About',
          to: '/about',
          icon: OmniBadge,
          subTitle: "Why we're building Omni",
        },
        {
          label: 'Careers',
          to: 'https://exploreomni.rippling-ats.com/',
          icon: TbBriefcase,
          subTitle: 'Build Omni with us',
          externalLink: true,
        },
      ],
    ],
  },
]
